/**
 * V5 advance-mode billing helpers for Updated TimeSheet Code Allocation.
 * Legacy shifts (is_v5_multi_assignment !== true) must keep existing behaviour.
 */

import {
  buildNdisSelectedOption,
} from "pages/Rostering/WorkerShift/components/advanceModeSplitDefaults";
import moment from "moment";
import {
  calculateDuration,
  convertTimeToDatetimeLocal,
  customFormatDate,
  embedDateWithTime,
  extractTimeFromDateTime,
  isCheckNextDayShift,
  parseBooleanVal,
  timeToQuantity,
  formatAmount,
  getInvoiceTimesheetLines,
  rosterInvoiceTimesheetTotal,
  truncateDecimal,
} from "Utils/utils";
import {
  isAdvanceSameClockTimeFullDay,
  isAdvanceUnallocatedSameClockSegment,
} from "pages/Rostering/WorkerShift/components/advanceModeValidation";
import { resolvePersistedBreakdownUnitRate } from "../../../utils/breakdownFormHydration";
import { formatAppliedRateExact } from "../../../utils/codeAllocationRates";

export const isV5MultiAssignmentTimesheet = (shiftDetails) =>
  shiftDetails?.is_v5_multi_assignment === true ||
  shiftDetails?.is_multi_assignment === true;

export const isKmOptedIn = (row) =>
  Boolean(row?.apply_km ?? row?.km_for_invoicing);

/** Share-based preview — prefer API travel_amount_preview / travel_amount when present. */
export const travelShareAmount = (row) => {
  if (row?.travel_amount_preview != null && row.travel_amount_preview !== "") {
    return Number(row.travel_amount_preview);
  }
  if (row?.travel_amount != null && row.travel_amount !== "") {
    return Number(row.travel_amount);
  }
  if (!isKmOptedIn(row)) return 0;
  const km = parseFloat(row?.km) || 0;
  const rate = parseFloat(row?.unit_price ?? row?.km_price) || 0;
  return truncateDecimal(km * rate);
};

export const buildParticipantTravelSavePayload = (rows = []) =>
  rows.filter(isKmOptedIn).map((row) => ({
    participant_id: row.participant_id,
    km: row.km,
    km_item_id: row.km_item_id,
    km_price: row.km_price,
    km_alternative_rate: Boolean(row.km_alternative_rate),
  }));

export const getParticipantTravelRows = (shiftDetails) => {
  const rows =
    shiftDetails?.participant_travel ??
    shiftDetails?.v5_shift_billing?.participant_travel ??
    [];
  return Array.isArray(rows) ? rows : [];
};

export const getOptedInParticipantTravel = (shiftDetails) =>
  getParticipantTravelRows(shiftDetails).filter(isKmOptedIn);

/**
 * Resolve the travel line-item selection for a code-allocation travel row.
 * AI Suggested (default_km_code null + no km_item_id) → suggested_travel_item.
 */
export const resolveTravelItemSelection = (row) => {
  if (!row) return null;
  const items = Array.isArray(row.travel_items) ? row.travel_items : [];

  if (row.km_item_id != null) {
    const byId = items.find(
      (item) => String(item?.id) === String(row.km_item_id)
    );
    if (byId) return byId;
  }

  if (row.default_km_code) {
    const byCode = items.find(
      (item) =>
        String(item?.support_item_number) === String(row.default_km_code)
    );
    if (byCode) return byCode;
  }

  if (
    (row.default_km_code === null || row.default_km_code === undefined) &&
    row.km_item_id == null &&
    row.suggested_travel_item
  ) {
    return row.suggested_travel_item;
  }

  return null;
};

/** Prefill km_item_id from AI suggested / default_km_code when missing; keep API km (incl. prefetched share). */
export const hydrateParticipantTravelRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    if (!isKmOptedIn(row)) return { ...row };
    let next = { ...row };
    // Keep prefetched / saved km from API as-is (null stays null).
    if (next.km_item_id != null) return next;
    const resolved = resolveTravelItemSelection(next);
    if (!resolved?.id) return next;
    return {
      ...next,
      km_item_id: resolved.id,
      km_price:
        next.km_price != null && next.km_price !== ""
          ? next.km_price
          : resolved.price ?? null,
    };
  });

/** Helper caption for timesheet KM → participant share prefetch. */
export const formatTimesheetKmPrefetchCaption = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  const sample = list.find(
    (row) =>
      isKmOptedIn(row) &&
      row?.timesheet_km_total != null &&
      Number(row.timesheet_km_total) > 0
  );
  if (!sample) return null;
  const total = Number(sample.timesheet_km_total);
  const optIn = Math.max(
    1,
    parseInt(sample.opt_in_count ?? list.filter(isKmOptedIn).length, 10) || 1
  );
  const share =
    sample.km_prefetched && sample.km != null && sample.km !== ""
      ? Number(sample.km)
      : truncateDecimal(total / optIn);
  return `From timesheets: ${total} km ÷ ${optIn} participant${
    optIn === 1 ? "" : "s"
  } = ${share} km each`;
};

export const toTravelItemSelectOption = (item) => {
  if (!item) return null;
  const number = item.support_item_number || item.number || "";
  const name = item.support_item_name || item.name || "";
  return {
    ...item,
    label: name && number ? `${name} - ${number}` : name || number || "Travel item",
    value: item.id,
    price: item.price,
    support_item_number: number,
    support_item_name: name,
  };
};

/** Find a participant_travel row for a billing participant (create-invoice UI). */
export const findParticipantTravelForParticipant = (
  participantTravel = [],
  participant
) => {
  if (!participant || !Array.isArray(participantTravel)) return null;
  const id = participant?.id ?? participant?.pt_id;
  const uuid = participant?.uuid;
  return (
    participantTravel.find(
      (row) =>
        (id != null && String(row?.participant_id) === String(id)) ||
        (uuid && String(row?.participant_uuid) === String(uuid))
    ) ?? null
  );
};

/**
 * Sort billing payload / display rows chronologically (mirrors BE sortBillingRowsByStartTime).
 * Sleepover parent with is_active_hours stays paired with the immediately following child.
 */
export const sortBillingRowsByStartTime = (rows = []) => {
  const list = (Array.isArray(rows) ? rows : []).filter(
    (row) => row != null && typeof row === "object"
  );

  const startTs = (row) => {
    const raw = row?.start_time;
    if (raw == null || raw === "") return Number.MAX_SAFE_INTEGER;
    const parsed = moment(raw);
    return parsed.isValid() ? parsed.valueOf() : Number.MAX_SAFE_INTEGER;
  };

  const isSleepoverParentWithActiveHours = (row) =>
    parseBooleanVal(row?.is_active_hours) &&
    parseBooleanVal(row?.sleepover ?? row?.is_sleepover);

  const groups = [];
  for (let i = 0; i < list.length; i += 1) {
    const row = list[i];
    if (isSleepoverParentWithActiveHours(row) && i + 1 < list.length) {
      groups.push({
        start: startTs(row),
        tie: Number(row?.split_shift_id ?? row?.id ?? 0) || 0,
        uuid: String(row?.uuid ?? ""),
        rows: [row, list[i + 1]],
      });
      i += 1;
      continue;
    }
    groups.push({
      start: startTs(row),
      tie: Number(row?.split_shift_id ?? row?.id ?? 0) || 0,
      uuid: String(row?.uuid ?? ""),
      rows: [row],
    });
  }

  groups.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    if (a.tie !== b.tie) return a.tie - b.tie;
    return a.uuid.localeCompare(b.uuid);
  });

  return groups.flatMap((group) => group.rows);
};

/** Normalize V5 billing detail so lines/splits are chronological for display. */
export const sortV5BillingDetailChronologically = (shiftDetails) => {
  if (!shiftDetails || !isV5MultiAssignmentTimesheet(shiftDetails)) {
    return shiftDetails;
  }

  const billing = shiftDetails.v5_shift_billing;
  const sortedLines = sortBillingRowsByStartTime(
    billing?.lines ?? shiftDetails.invoiceTimesheets ?? []
  );
  const sortedSplits = sortBillingRowsByStartTime(billing?.splits ?? []);

  return {
    ...shiftDetails,
    ...(billing
      ? {
          v5_shift_billing: {
            ...billing,
            lines: sortedLines,
            splits: sortedSplits,
          },
        }
      : {}),
    invoiceTimesheets: sortedLines,
  };
};

export const getBillingLines = (shiftDetails) => {
  if (isV5MultiAssignmentTimesheet(shiftDetails)) {
    return (
      shiftDetails?.v5_shift_billing?.lines ??
      shiftDetails?.invoiceTimesheets ??
      []
    );
  }
  return shiftDetails?.invoiceTimesheets ?? [];
};

const billingLineHasPersistedCode = (row) =>
  Boolean(
    row?.invoice_item_id ||
      row?.custom_line_item_uuid ||
      row?.custom_line_item?.uuid
  );

const normalizeBreakdownShiftType = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const breakdownWindowKey = (row) => {
  const startClock = normalizeClockTimeInput(
    extractTimeFromDateTime(row?.start_time)
  );
  const endClock = normalizeClockTimeInput(
    extractTimeFromDateTime(row?.end_time)
  );
  return `${startClock || ""}|${endClock || ""}`;
};

/** First-start / last-end clocks across breakdown rows (HH:mm|HH:mm). */
export const breakdownSpanKey = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const starts = [];
  const ends = [];
  rows.forEach((row) => {
    const startClock = normalizeClockTimeInput(
      extractTimeFromDateTime(row?.start_time)
    );
    const endClock = normalizeClockTimeInput(
      extractTimeFromDateTime(row?.end_time)
    );
    if (startClock) starts.push(startClock);
    if (endClock) ends.push(endClock);
  });
  if (starts.length === 0 || ends.length === 0) return "";
  starts.sort();
  ends.sort();
  return `${starts[0]}|${ends[ends.length - 1]}`;
};

/**
 * True when every persisted breakdown row shares the same clock window.
 * That is the corrupted "all rows = full shift" case — preview may repair it.
 * A single row, or rows with distinct day/evening/night clocks, are not collapsed.
 */
export const savedBreakdownWindowsAreCollapsedDuplicates = (savedLines) => {
  if (!Array.isArray(savedLines) || savedLines.length < 2) {
    return false;
  }
  const keys = savedLines.map(breakdownWindowKey);
  if (keys.some((key) => key === "|")) {
    return true;
  }
  return keys.every((key) => key === keys[0]);
};

/**
 * Match a preview day/evening/night segment to a saved billing line.
 * Prefer unused shift_type match, then unused index. Marks matches in usedIndexes.
 */
const findSavedBillingLineForPreviewSegment = (
  previewRow,
  savedLines,
  index,
  usedIndexes
) => {
  if (!Array.isArray(savedLines) || savedLines.length === 0) return null;

  const previewType = normalizeBreakdownShiftType(previewRow?.shift_type);
  if (previewType) {
    const typeMatchIdx = savedLines.findIndex(
      (line, lineIdx) =>
        !usedIndexes.has(lineIdx) &&
        normalizeBreakdownShiftType(line?.shift_type) === previewType
    );
    if (typeMatchIdx >= 0) {
      usedIndexes.add(typeMatchIdx);
      return savedLines[typeMatchIdx];
    }
  }

  if (!usedIndexes.has(index) && savedLines[index]) {
    usedIndexes.add(index);
    return savedLines[index];
  }

  return null;
};

/**
 * Basic auto-breakdown preview only has segment windows + catalogue suggestions.
 * Overlay persisted code/rate fields (incl. alternate_rate) from saved billing
 * lines so reload/save refresh does not drop "Apply Alternative Rate".
 *
 * Persisted start/end/duration are the source of truth after the user edits
 * segment clocks and saves. Preview windows are used to repair collapsed
 * "every row = full shift" lines, or when the preferred invoice window
 * (schedule vs actual) no longer matches the saved span.
 * No-ops when counts differ — avoids remapping bugs.
 */
export const mergePreviewWithSavedBillingLines = (
  previewRows,
  savedLines
) => {
  if (!Array.isArray(previewRows) || previewRows.length === 0) {
    return Array.isArray(savedLines) && savedLines.length > 0
      ? savedLines
      : previewRows ?? [];
  }
  if (!Array.isArray(savedLines) || savedLines.length === 0) {
    return previewRows;
  }
  // Structural mismatch (e.g. holiday collapse) — do not guess mapping.
  if (previewRows.length !== savedLines.length) {
    return previewRows;
  }

  const previewSpan = breakdownSpanKey(previewRows);
  const savedSpan = breakdownSpanKey(savedLines);
  const preferredWindowChanged =
    Boolean(previewSpan) && Boolean(savedSpan) && previewSpan !== savedSpan;

  const usePreviewWindows =
    savedBreakdownWindowsAreCollapsedDuplicates(savedLines) ||
    preferredWindowChanged;
  const hasPersistedCodes = savedLines.some(billingLineHasPersistedCode);
  if (!hasPersistedCodes && usePreviewWindows) {
    return previewRows;
  }

  const usedIndexes = new Set();
  return previewRows.map((preview, index) => {
    const saved = findSavedBillingLineForPreviewSegment(
      preview,
      savedLines,
      index,
      usedIndexes
    );
    if (!saved) {
      return preview;
    }
    if (!billingLineHasPersistedCode(saved) && usePreviewWindows) {
      return preview;
    }

    const customUuid =
      saved.custom_line_item_uuid ?? saved.custom_line_item?.uuid ?? null;
    const previewItemNumbers =
      preview?.item_numbers && typeof preview.item_numbers === "object"
        ? preview.item_numbers
        : {};
    const previewSelected =
      previewItemNumbers?.selected &&
      typeof previewItemNumbers.selected === "object"
        ? previewItemNumbers.selected
        : null;
    const isAlternate = parseBooleanVal(saved.alternate_rate);
    const selected = billingLineHasPersistedCode(saved)
      ? {
          ...(previewSelected || {}),
          id: saved.invoice_item_id ?? previewSelected?.id ?? null,
          support_item_number:
            saved.ndis_support_item_number ??
            previewSelected?.support_item_number ??
            null,
          support_item_name:
            saved.ndis_support_item_name ??
            previewSelected?.support_item_name ??
            null,
          price: previewSelected?.price ?? saved.invoice_price ?? saved.ndis_rate,
          ...(isAlternate
            ? {
                is_alternate: true,
                alternate_price: saved.ndis_rate ?? saved.invoice_price ?? null,
              }
            : {}),
        }
      : previewSelected;

    // Mixed alternate/actual: do not paint saved 0/null onto actual rows when
    // preview still knows the catalogue price (untouched segments).
    const unitRate = billingLineHasPersistedCode(saved)
      ? resolvePersistedBreakdownUnitRate({
          ...saved,
          alternate_rate: isAlternate,
          item_numbers: {
            items: Array.isArray(previewItemNumbers?.items)
              ? previewItemNumbers.items
              : Array.isArray(saved.item_numbers?.items)
                ? saved.item_numbers.items
                : [],
            selected,
          },
        })
      : null;

    const startTime = usePreviewWindows
      ? preview.start_time
      : saved.start_time ?? preview.start_time;
    const endTime = usePreviewWindows
      ? preview.end_time
      : saved.end_time ?? preview.end_time;

    return {
      ...preview,
      // Preserve ids so form scope keys stay stable across preview refresh.
      id: saved.id ?? preview.id,
      uuid: saved.uuid ?? preview.uuid,
      invoice_item_id: saved.invoice_item_id ?? null,
      custom_line_item_uuid: customUuid,
      custom_line_item: saved.custom_line_item ?? preview.custom_line_item,
      alternate_rate: isAlternate,
      ndis_rate: unitRate ?? saved.ndis_rate ?? preview.ndis_rate,
      invoice_price: unitRate ?? saved.invoice_price ?? preview.invoice_price,
      ndis_support_item_number:
        saved.ndis_support_item_number ?? preview.ndis_support_item_number,
      ndis_support_item_name:
        saved.ndis_support_item_name ?? preview.ndis_support_item_name,
      sleepover: saved.sleepover ?? saved.is_sleepover ?? preview.sleepover,
      is_sleepover:
        saved.is_sleepover ?? saved.sleepover ?? preview.is_sleepover,
      ratio: saved.ratio ?? preview.ratio,
      children: Array.isArray(saved.children)
        ? saved.children
        : preview.children,
      start_time: startTime,
      end_time: endTime,
      duration: usePreviewWindows
        ? preview.duration ?? saved.duration
        : saved.duration ?? preview.duration,
      shift_type: saved.shift_type ?? preview.shift_type,
      item_numbers: {
        items: Array.isArray(previewItemNumbers?.items)
          ? previewItemNumbers.items
          : Array.isArray(saved.item_numbers?.items)
            ? saved.item_numbers.items
            : [],
        selected,
      },
    };
  });
};

export const getBillingSplits = (shiftDetails) => {
  if (isV5MultiAssignmentTimesheet(shiftDetails)) {
    return shiftDetails?.v5_shift_billing?.splits ?? [];
  }
  return Array.isArray(shiftDetails?.split_shift) ? shiftDetails.split_shift : [];
};

/** Split-style billing UI (legacy split shift or V5 advance). */
export const isSplitBillingMode = (shiftDetails) =>
  Boolean(
    shiftDetails?.billing_mode === "split" ||
      shiftDetails?.billing_mode === "advance" ||
      shiftDetails?.is_split_shift ||
      isV5MultiAssignmentTimesheet(shiftDetails)
  );

/** Legacy split shift only (not V5 advance). */
export const isLegacySplitShift = (shiftDetails) =>
  Boolean(shiftDetails?.is_split_shift && !isV5MultiAssignmentTimesheet(shiftDetails));

/**
 * Legacy basic split-shift timesheets are editable on Timesheet Details
 * (scheduled/actual times). Warnings and field locks key off this flag.
 */
export const isLegacySplitShiftTimeLocked = (_shiftDetails) => false;

/** Legacy split shifts default breakdown on. Toggle remains editable in Code Allocation. */
export const isBreakdownToggleLocked = (shiftDetails) => isLegacySplitShift(shiftDetails);

export const getDefaultInvoiceBreakdown = (shiftDetails) => {
  // Basic split always uses per-segment CA. Do not let a stored/SP
  // default_timesheet_breakdown=false collapse the save to a single line.
  if (isBreakdownToggleLocked(shiftDetails)) {
    return true;
  }
  if (shiftDetails?.default_timesheet_breakdown != null) {
    return Boolean(shiftDetails.default_timesheet_breakdown);
  }
  if (isV5MultiAssignmentTimesheet(shiftDetails)) {
    return true;
  }
  return false;
};

/** Show split accordions when breakdown toggle is on (always for legacy split and V5 advance). */
export const shouldShowSplitBreakdownAccordion = (shiftDetails, invoiceBreakdownEnabled) => {
  if (!isSplitBillingMode(shiftDetails)) return false;
  if (getBillingSplits(shiftDetails).length === 0 && getBillingLines(shiftDetails).length === 0) {
    return false;
  }
  if (isV5MultiAssignmentTimesheet(shiftDetails) || isBreakdownToggleLocked(shiftDetails)) {
    return true;
  }
  return Boolean(invoiceBreakdownEnabled);
};

/** Global service category row — hidden for legacy split and V5 advance (per-breakdown instead). */
export const shouldShowServiceCategoryFields = (shiftDetails) => {
  if (isV5MultiAssignmentTimesheet(shiftDetails)) return false;
  if (shiftDetails?.is_split_shift) return false;
  return true;
};

export const isBillingLocked = (shiftDetails) =>
  shiftDetails?.v5_shift_billing?.is_locked === true ||
  shiftDetails?.billing?.is_locked === true ||
  parseBooleanVal(shiftDetails?.roster_invoice_generated);

export const getBillingInvoiceNumber = (shiftDetails) => {
  const fromString = String(shiftDetails?.invoice_number ?? "").trim();
  if (fromString) return fromString;

  const nested = []
    .concat(shiftDetails?.invoice_numbers ?? [])
    .concat(shiftDetails?.billing?.invoice_numbers ?? [])
    .concat(shiftDetails?.v5_shift_billing?.invoice_numbers ?? [])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  return nested.length ? [...new Set(nested)].join(", ") : null;
};

export const hasBillingLines = (shiftDetails) => getBillingLines(shiftDetails).length > 0;

export const getShiftUuidForBilling = (shiftDetails) =>
  shiftDetails?.shift_uuid ??
  shiftDetails?.v5_shift_billing?.shift_uuid ??
  shiftDetails?.shift?.uuid ??
  null;

/**
 * Location state for navigating from Code Allocation → Schedule with the
 * shift editor modal open. Schedule reads `openShift` and clears it after use.
 */
export const buildScheduleOpenShiftState = (shiftDetails) => {
  const uuid =
    getShiftUuidForBilling(shiftDetails) ?? shiftDetails?.uuid ?? null;
  if (!uuid) return null;

  const isAdvance =
    parseBooleanVal(shiftDetails?.is_multi_assignment) ||
    parseBooleanVal(shiftDetails?.is_v5_multi_assignment) ||
    shiftDetails?.billing_mode === "advance";

  const shiftDate =
    shiftDetails?.shift_date ??
    shiftDetails?.time_sheet_date ??
    shiftDetails?.date ??
    null;

  return {
    openShift: {
      uuid,
      is_multi_assignment: Boolean(isAdvance),
      date: shiftDate,
    },
  };
};

export const getTimesheetModeLabel = (shiftDetails) => {
  if (
    shiftDetails?.billing_mode === "advance" ||
    isV5MultiAssignmentTimesheet(shiftDetails)
  ) {
    return "Advance";
  }
  if (shiftDetails?.billing_mode === "split" || shiftDetails?.is_split_shift) {
    return "Split shift";
  }
  return "Standard";
};

export const parseRatioParts = (ratio) => {
  if (!ratio) return null;
  const parts = String(ratio).split(":");
  const worker = parseInt(parts[0], 10);
  const participant = parseInt(parts[1], 10);
  if (
    Number.isNaN(worker) ||
    worker < 1 ||
    Number.isNaN(participant) ||
    participant < 1
  ) {
    return null;
  }
  return { worker, participant };
};

export const parseRatioParticipantCount = (ratio) => {
  const parts = parseRatioParts(ratio);
  return parts ? parts.participant : null;
};

/** Catalog ÷ this divisor = applied rate (worker × catalog ÷ participant). */
export const parseRatioAppliedDivisor = (ratio, fallback = 1) => {
  const parts = parseRatioParts(ratio);
  if (!parts) return fallback;
  return parts.participant / parts.worker;
};

/** Applied rate: catalog × worker ÷ participant (e.g. 5:5 → full rate). */
export const applyWorkerParticipantRatio = (baseRate, ratio) => {
  const parts = parseRatioParts(ratio);
  if (!parts) return null;
  const rate = Number(baseRate);
  if (Number.isNaN(rate)) return null;
  return (rate * parts.worker) / parts.participant;
};

/** Stable key for a breakdown segment's billed participant(s) — advance mode sleepover rules. */
export const getBreakdownParticipantKey = (shiftDetails, index, breakdownRow) => {
  const participants = getBillingParticipantsForRow(
    shiftDetails,
    index,
    breakdownRow
  );
  if (!participants.length) return `segment-${index}`;
  if (participants.length === 1) {
    const participant = participants[0];
    return String(participant?.id ?? participant?.uuid ?? `segment-${index}`);
  }
  return participants
    .map((participant) => participant?.id ?? participant?.uuid)
    .filter(Boolean)
    .sort()
    .join("|");
};

export const validateAdvanceSleepoverPerParticipant = (
  shiftDetails,
  breakdownRows = [],
  watchFormData = {}
) => {
  if (!isV5MultiAssignmentTimesheet(shiftDetails)) return true;

  const seen = new Map();
  for (let i = 0; i < breakdownRows.length; i++) {
    if (!parseBooleanVal(watchFormData?.[`sleepover_${i}`])) continue;
    const key = getBreakdownParticipantKey(shiftDetails, i, breakdownRows[i]);
    if (seen.has(key)) {
      return "Only one sleepover breakdown is allowed per participant.";
    }
    seen.set(key, i);
  }
  return true;
};

export const resolveBreakdownDefaultRatio = (
  shiftDetails,
  index,
  breakdownRow,
  billingSplitMeta
) =>
  billingSplitMeta?.ratio ??
  breakdownRow?.ratio ??
  shiftDetails?.ratio ??
  null;

export const formatBillingDurationLabel = (duration) => {
  if (!duration) return null;
  const parts = String(duration).split(":");
  const h = parts[0] || "00";
  const m = parts[1] || "00";
  return `${h}h ${m}m`;
};

export const formatParticipantDisplayName = (rawName) => {
  if (!rawName) return "";
  return String(rawName)
    .trim()
    .split(/\s+/)
    .map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(" ");
};

export const getParticipantDisplayName = (participant) => {
  if (!participant) return "";
  const raw =
    `${participant?.first_name || ""} ${participant?.last_name || ""}`.trim() ||
    participant?.name ||
    participant?.participant_name ||
    (typeof participant?.label === "string" ? participant.label : "") ||
    "";
  return formatParticipantDisplayName(raw);
};

export const formatBillingParticipantNames = (participants = []) =>
  participants.map((p) => getParticipantDisplayName(p)).filter(Boolean).join(", ");

export const getParticipantInitials = (participant) => {
  if (!participant) return "?";
  const first = (participant.first_name || "").charAt(0);
  const last = (participant.last_name || "").charAt(0);
  const initials = `${first}${last}`.toUpperCase();
  return initials || "?";
};

/**
 * Top-level invoice rows: include parents and flat child rows with a billable total.
 * Nested `children` are handled separately — always shown when returned by the API.
 */
const isVisibleTopLevelInvoiceLine = (line) =>
  line?.parent_id == null || parseFloat(line?.total_amount) > 0;

/**
 * V5 create-invoice API nests active-hours rows under sleepover parents in `children`.
 * Flatten so billable breakdown shows every configured line (including $0 active hours).
 */
const flattenInvoiceLinesWithChildren = (lines) => {
  if (!Array.isArray(lines)) return [];

  const visible = [];

  lines.forEach((line) => {
    if (isVisibleTopLevelInvoiceLine(line)) {
      visible.push(line);
    }

    const children = Array.isArray(line?.children) ? line.children : [];
    children.forEach((child) => {
      visible.push({
        ...child,
        split_shift_id: child.split_shift_id ?? line.split_shift_id,
        parent_id: child.parent_id ?? line.id ?? line.parent_id,
      });
    });
  });

  return visible;
};

/** Visible invoice lines for create-invoice / preview tables. */
export const getVisibleInvoiceLines = (shiftDetails) => {
  const lines =
    shiftDetails?.invoice_timesheets ??
    shiftDetails?.invoiceTimesheets ??
    getBillingLines(shiftDetails);
  return flattenInvoiceLinesWithChildren(lines);
};

/**
 * Resolve which participant(s) an invoice line belongs to — display only.
 * Handles V5 split metadata, legacy 1:1 line/participant rows, and basic shifts.
 */
export const resolveInvoiceLineParticipants = (
  shiftDetails,
  lineIndex,
  line,
  visibleLines = null
) => {
  const shiftParticipants = shiftDetails?.participants ?? [];
  const lines = visibleLines ?? getVisibleInvoiceLines(shiftDetails);
  const rowParticipants = getBillingParticipantsForRow(
    shiftDetails,
    lineIndex,
    line
  );

  if (rowParticipants.length === 1) {
    return rowParticipants;
  }

  if (isV5MultiAssignmentTimesheet(shiftDetails) && rowParticipants.length > 1) {
    return rowParticipants;
  }

  if (
    shiftParticipants.length > 1 &&
    lines.length === shiftParticipants.length
  ) {
    const visibleIndex = lines.indexOf(line);
    if (visibleIndex >= 0 && visibleIndex < shiftParticipants.length) {
      return [shiftParticipants[visibleIndex]];
    }
  }

  if (shiftParticipants.length === 1) {
    return shiftParticipants;
  }

  if (rowParticipants.length > 0) {
    return rowParticipants;
  }

  return shiftParticipants.length ? shiftParticipants : [];
};

/**
 * Group visible invoice lines under participants for create-invoice UI.
 * Does not alter billing data — display grouping only.
 */
export const groupInvoiceLinesByParticipant = (shiftDetails, visibleLines = null) => {
  const lines = visibleLines ?? getVisibleInvoiceLines(shiftDetails);
  if (!lines.length) return [];

  const groupsMap = new Map();
  const allLines =
    shiftDetails?.invoice_timesheets ??
    shiftDetails?.invoiceTimesheets ??
    getBillingLines(shiftDetails);
  const lineIndexOf = (line) => {
    const idx = Array.isArray(allLines) ? allLines.indexOf(line) : -1;
    return idx >= 0 ? idx : lines.indexOf(line);
  };

  const ensureGroup = (key, participant, participants, isShared = false) => {
    if (!groupsMap.has(key)) {
      const label =
        participants.length > 1
          ? formatBillingParticipantNames(participants)
          : getParticipantDisplayName(participant) || "Participant";
      groupsMap.set(key, {
        participantKey: key,
        participant: participant ?? participants[0] ?? null,
        participants,
        participantLabel: isShared ? "All participants" : label || "Participant",
        initials: getParticipantInitials(participant ?? participants[0]),
        isShared,
        lines: [],
      });
    }
    return groupsMap.get(key);
  };

  lines.forEach((line) => {
    const lineIndex = lineIndexOf(line);
    const rowParticipants = resolveInvoiceLineParticipants(
      shiftDetails,
      lineIndex,
      line,
      lines
    );

    if (rowParticipants.length === 0) {
      ensureGroup("unassigned", null, [], false).lines.push({ line, lineIndex });
      return;
    }

    if (rowParticipants.length === 1) {
      const participant = rowParticipants[0];
      ensureGroup(
        String(participant?.id ?? participant?.pt_id ?? lineIndex),
        participant,
        rowParticipants,
        false
      ).lines.push({ line, lineIndex });
      return;
    }

    const compositeKey = rowParticipants
      .map((p) => p?.id ?? p?.pt_id)
      .filter((id) => id != null)
      .join("-");
    ensureGroup(
      compositeKey || `shared-${lineIndex}`,
      rowParticipants[0],
      rowParticipants,
      true
    ).lines.push({ line, lineIndex });
  });

  return Array.from(groupsMap.values());
};

/**
 * Groups breakdown row indices under participants for clearer billing UI.
 * Does not alter billing data — display grouping only.
 */
export const groupBreakdownIndicesByParticipant = (
  shiftDetails,
  breakdownRows = []
) => {
  if (!Array.isArray(breakdownRows) || breakdownRows.length === 0) return [];

  const groupsMap = new Map();
  const shiftParticipants = shiftDetails?.participants ?? [];

  const ensureGroup = (key, participant, participants, ratio) => {
    if (!groupsMap.has(key)) {
      const label =
        participants.length > 1
          ? formatBillingParticipantNames(participants)
          : getParticipantDisplayName(participant) || "Participant";
      groupsMap.set(key, {
        participantKey: key,
        participant: participant ?? participants[0] ?? null,
        participants,
        participantLabel: label || "Participant",
        initials: getParticipantInitials(participant ?? participants[0]),
        ratio: ratio ?? null,
        indices: [],
      });
    }
    return groupsMap.get(key);
  };

  breakdownRows.forEach((item, index) => {
    const splitMeta = getBillingSplitMeta(shiftDetails, index, item);
    const rowParticipants = getBillingParticipantsForRow(
      shiftDetails,
      index,
      item
    );

    if (rowParticipants.length === 0 && shiftParticipants.length === 1) {
      const p = shiftParticipants[0];
      ensureGroup(
        String(p?.id ?? p?.pt_id ?? "0"),
        p,
        [p],
        splitMeta?.ratio
      ).indices.push(index);
      return;
    }

    if (rowParticipants.length === 0) {
      ensureGroup(
        "shared",
        shiftParticipants[0] ?? null,
        shiftParticipants,
        splitMeta?.ratio
      ).indices.push(index);
      return;
    }

    if (rowParticipants.length === 1) {
      const p = rowParticipants[0];
      ensureGroup(
        String(p?.id ?? p?.pt_id ?? index),
        p,
        rowParticipants,
        splitMeta?.ratio
      ).indices.push(index);
      return;
    }

    const compositeKey = rowParticipants
      .map((p) => p?.id ?? p?.pt_id)
      .filter((id) => id != null)
      .join("-");
    ensureGroup(
      compositeKey || `multi-${index}`,
      rowParticipants[0],
      rowParticipants,
      splitMeta?.ratio
    ).indices.push(index);
  });

  return Array.from(groupsMap.values());
};

/**
 * Unique billed participants across grouping cards.
 * Basic group shifts often share one breakdown (ratio 1:3) so groups.length
 * is 1 even when three people are on the shift — count people, not cards.
 */
export const countUniqueBillingParticipants = (
  groups = [],
  shiftDetails = null
) => {
  const ids = new Set();
  const addParticipant = (participant) => {
    if (!participant) return;
    const id =
      participant.id ?? participant.uuid ?? participant.pt_id ?? null;
    if (id != null && String(id).trim() !== "") {
      ids.add(String(id));
    }
  };

  (Array.isArray(groups) ? groups : []).forEach((group) => {
    if (Array.isArray(group?.participants) && group.participants.length > 0) {
      group.participants.forEach(addParticipant);
    } else {
      addParticipant(group?.participant);
    }
  });

  if (ids.size > 0) return ids.size;

  const shiftParticipants = shiftDetails?.participants;
  if (Array.isArray(shiftParticipants) && shiftParticipants.length > 0) {
    return shiftParticipants.length;
  }

  return Array.isArray(groups) ? groups.length : 0;
};

/** Category item name (hi/s/st/hit) for NDIS catalogue fetch in advance billing. */
export const resolveAdvanceInvoiceCategoryName = (
  item,
  shiftDetails,
  billingParticipants = []
) => {
  if (item?.invoice_category_name) return item.invoice_category_name;
  if (shiftDetails?.invoice_category_name) return shiftDetails.invoice_category_name;
  const participants = billingParticipants?.length
    ? billingParticipants
    : shiftDetails?.participants ?? [];
  const match = participants.find((p) => p?.roster_invoicing_item);
  return match?.roster_invoicing_item ?? null;
};

export const toBreakdownNdisSelectionShape = (item) => ({
  invoice_item_id: item?.invoice_item_id ?? null,
  custom_line_item_uuid:
    item?.custom_line_item_uuid ?? item?.custom_line_item?.uuid ?? null,
  support_item_number:
    item?.ndis_support_item_number ??
    item?.support_item_number ??
    item?.custom_line_item?.unique_code ??
    null,
  support_item_name:
    item?.ndis_support_item_name ??
    item?.support_item_name ??
    item?.custom_line_item?.description ??
    null,
});

export const formatLineItemSelectOption = (opt) => {
  if (!opt) return null;
  const name = opt.name ?? opt.support_item_name ?? "";
  const number = opt.number ?? opt.support_item_number ?? "";
  const label =
    opt.label && String(opt.label).includes(" - ")
      ? opt.label
      : `${name}${number ? ` - ${number}` : ""}`.trim();
  return {
    label: label || "Selected line item",
    value: opt.value,
    price: opt.price,
    number,
    name: name || label,
    isCustom: Boolean(opt.isCustom),
  };
};

/** Ensure saved shift/billing line item appears in breakdown dropdown options. */
export const mergeBreakdownSavedLineItemOption = (options = [], item) => {
  if (!item) return options;
  const shape = toBreakdownNdisSelectionShape(item);
  if (shape.invoice_item_id == null && !shape.support_item_number) {
    return options;
  }
  const saved = buildNdisSelectedOption(shape, options);
  if (!saved) return options;
  const exists = options.some(
    (o) =>
      String(o.value) === String(saved.value) ||
      (saved.number != null &&
        saved.number !== "" &&
        String(o.number) === String(saved.number))
  );
  if (exists) return options;
  return [...options, formatLineItemSelectOption(saved)];
};

/** Each split/breakdown row's schedule window (prefer split definition over billing line). */
export const resolveBreakdownScheduleTimes = (item, billingSplitMeta) => ({
  start:
    billingSplitMeta?.start_time ??
    item?.start_time ??
    null,
  end:
    billingSplitMeta?.end_time ??
    item?.end_time ??
    null,
});

/** Accordion header date/time labels from split segment times and optional shift bounds. */
export const formatBreakdownScheduleHeader = (
  breakdownStartDateTime,
  breakdownEndDateTime,
  { shiftStartDateTime = null, shiftEndDateTime = null } = {}
) => {
  if (!breakdownStartDateTime || !breakdownEndDateTime) {
    return { dateRange: "", timeRange: "" };
  }
  const startTimeStr = extractTimeFromDateTime(breakdownStartDateTime);
  const endTimeStr = extractTimeFromDateTime(breakdownEndDateTime);
  const dateRangeStart = shiftStartDateTime || breakdownStartDateTime;
  const dateRangeEnd = shiftEndDateTime || breakdownEndDateTime;
  const startDate = customFormatDate(dateRangeStart);
  const endDate = customFormatDate(dateRangeEnd);
  if (!startTimeStr || !endTimeStr || !startDate || !endDate) {
    return { dateRange: "", timeRange: "" };
  }
  return {
    dateRange: `${startDate} - ${endDate}`,
    timeRange: `${moment(startTimeStr, "HH:mm").format("hh:mm A")} - ${moment(endTimeStr, "HH:mm").format("hh:mm A")}`,
  };
};

const findBillingLineForSplit = (billingLines, splitRow, index) => {
  if (!Array.isArray(billingLines)) return null;
  const splitId = splitRow?.id ?? splitRow?.split_shift_id;
  if (splitId != null) {
    const matched = billingLines.find(
      (line) =>
        line?.split_shift_id === splitId ||
        Number(line?.split_shift_id) === Number(splitId)
    );
    if (matched) return matched;
  }
  // Do not match by start/end alone — multiple participants often share the same
  // window, and a local split clone would incorrectly attach to the first line.
  return billingLines[index] ?? null;
};

export const getBillingSplitMeta = (shiftDetails, index, breakdownRow) => {
  const splits = getBillingSplits(shiftDetails);
  // Locally inserted segments already carry participant meta — prefer that so a
  // stale/foreign split_shift_id from another participant's line cannot win.
  if (
    breakdownRow?._clientKey &&
    breakdownRow?.billingSplitMeta?.participants?.length
  ) {
    return breakdownRow.billingSplitMeta;
  }
  const splitId = breakdownRow?.split_shift_id ?? breakdownRow?.id;
  if (splitId != null) {
    return splits.find((s) => s.id === splitId) ?? splits[index] ?? null;
  }
  return (
    breakdownRow?.billingSplitMeta ??
    splits[index] ??
    null
  );
};

/** Participants billed on a specific split row (V5) or all pivot participants (legacy). */
export const getBillingParticipantsForRow = (shiftDetails, index, breakdownRow) => {
  // Prefer explicit row participants first (critical for local "Split breakdown").
  if (
    isV5MultiAssignmentTimesheet(shiftDetails) &&
    (breakdownRow?.billingSplitMeta?.participants?.length ||
      breakdownRow?.participants?.length)
  ) {
    return (
      breakdownRow?.billingSplitMeta?.participants ?? breakdownRow.participants
    );
  }

  const splitMeta = getBillingSplitMeta(shiftDetails, index, breakdownRow);
  if (isV5MultiAssignmentTimesheet(shiftDetails) && splitMeta?.participants?.length) {
    return splitMeta.participants;
  }
  return shiftDetails?.participants ?? [];
};

/** Per-participant applied rate after group ratio / share. */
export const calculateAppliedPerParticipantRate = (
  hourlyRate,
  rateAmount,
  shiftDetails,
  index,
  breakdownRow,
  ratioOverride = null,
  exact = false
) => {
  const formatApplied = (value) =>
    exact ? formatAppliedRateExact(value) : Number(value).toFixed(2);

  const participants = getBillingParticipantsForRow(shiftDetails, index, breakdownRow);
  if (!participants.length) return "";

  const baseRate =
    hourlyRate !== "alternate" ? Number(hourlyRate) : Number(rateAmount);
  if (Number.isNaN(baseRate)) return "";

  const splitMeta = getBillingSplitMeta(shiftDetails, index, breakdownRow);
  const ratio =
    ratioOverride ?? splitMeta?.ratio ?? shiftDetails?.ratio ?? null;
  const ratioApplied = applyWorkerParticipantRatio(baseRate, ratio);
  if (ratioApplied != null) {
    return ratioApplied.toFixed(2);
  }

  if (isV5MultiAssignmentTimesheet(shiftDetails)) {
    const shareRatio = splitMeta?.participant_share_ratio;
    if (shareRatio != null && !Number.isNaN(Number(shareRatio))) {
      return formatApplied(baseRate * Number(shareRatio));
    }
    return formatApplied(baseRate / participants.length);
  }

  if (hourlyRate === "" || hourlyRate == null) return "";
  if (hourlyRate !== "alternate") {
    const result = baseRate / participants.length;
    return !Number.isNaN(result) ? formatApplied(result) : "";
  }
  if (rateAmount === "" || rateAmount == null) return "";
  const alt = Number(rateAmount) / participants.length;
  return !Number.isNaN(alt) ? formatApplied(alt) : "";
};

const mergeSplitBreakdownRow = (
  shiftDetails,
  index,
  splitRow,
  fallbackRow,
  matchedLine
) => {
  const isLocalInsert = Boolean(fallbackRow?._clientKey || splitRow?._clientKey);
  const splitCustomUuid =
    splitRow?.custom_line_item_uuid ?? splitRow?.custom_line_item?.uuid;
  const fallbackCustomUuid =
    fallbackRow?.custom_line_item_uuid ?? fallbackRow?.custom_line_item?.uuid;
  const matchedCustomUuid =
    matchedLine?.custom_line_item_uuid ?? matchedLine?.custom_line_item?.uuid;
  const mergedCustomUuid =
    splitCustomUuid ?? fallbackCustomUuid ?? matchedCustomUuid ?? null;

  // Never inherit another participant's persisted split id onto a local insert.
  const splitId = isLocalInsert
    ? null
    : splitRow?.id ??
      splitRow?.split_shift_id ??
      fallbackRow?.split_shift_id ??
      matchedLine?.split_shift_id ??
      null;

  const resolvedSleepover = parseBooleanVal(
    matchedLine?.sleepover ??
      matchedLine?.is_sleepover ??
      fallbackRow?.sleepover ??
      fallbackRow?.is_sleepover ??
      splitRow?.is_sleepover
  );
  const isParentLine =
    (matchedLine?.parent_id ??
      fallbackRow?.parent_id ??
      splitRow?.parent_id) == null;
  const resolveStaFlag = (value) =>
    parseBooleanVal(value) || value === 1 || String(value) === "1";
  const resolvedSta = resolveStaFlag(
    matchedLine?.is_sta ??
      fallbackRow?.is_sta ??
      splitRow?.is_sta ??
      (isParentLine ? shiftDetails?.is_sta ?? shiftDetails?.sta : false)
  );
  const resolvedChildren =
    matchedLine?.children ?? fallbackRow?.children ?? [];

  // Prefer split/local-row participants so a positional/index line cannot reassign
  // a newly split segment to another participant group.
  const rowParticipants =
    splitRow?.participants ??
    splitRow?.billingSplitMeta?.participants ??
    fallbackRow?.billingSplitMeta?.participants ??
    fallbackRow?.participants ??
    null;

  const preservedBillingSplitMeta =
    fallbackRow?.billingSplitMeta ?? splitRow?.billingSplitMeta ?? null;

  // Prefer invoice-line claim_type (API overlay on billing-allocation), then
  // split definition. Keeps Travel/NF2F visible for basic split Code Allocation.
  const resolvedClaimType =
    matchedLine?.claim_type ??
    fallbackRow?.claim_type ??
    splitRow?.claim_type ??
    null;
  const resolvedIsTravel =
    matchedLine?.is_travel ??
    fallbackRow?.is_travel ??
    splitRow?.is_travel ??
    (resolvedClaimType === "Travel" ? true : undefined);

  return {
    ...fallbackRow,
    ...splitRow,
    claim_type: resolvedClaimType,
    ...(resolvedIsTravel !== undefined ? { is_travel: resolvedIsTravel } : {}),
    split_shift_id: splitId,
    uuid: isLocalInsert
      ? null
      : fallbackRow?.uuid ?? matchedLine?.uuid ?? splitRow?.uuid ?? null,
    sleepover: resolvedSleepover,
    is_sleepover: resolvedSleepover,
    is_sta: resolvedSta,
    children: resolvedChildren,
    service_type_id:
      splitRow?.service_type_id ??
      splitRow?.invoice_service_type_id ??
      fallbackRow?.service_type_id ??
      fallbackRow?.invoice_service_type_id,
    service_type_name:
      splitRow?.service_type_name ??
      splitRow?.invoice_service_type_name ??
      fallbackRow?.service_type_name ??
      fallbackRow?.invoice_service_type_name,
    alternate_rate: parseBooleanVal(
      matchedLine?.alternate_rate ??
        fallbackRow?.alternate_rate ??
        splitRow?.alternate_rate
    ),
    ndis_rate:
      matchedLine?.ndis_rate ?? fallbackRow?.ndis_rate ?? splitRow?.ndis_rate,
    invoice_price:
      matchedLine?.invoice_price ??
      fallbackRow?.invoice_price ??
      splitRow?.invoice_price,
    total_amount:
      matchedLine?.total_amount ??
      fallbackRow?.total_amount ??
      splitRow?.total_amount,
    invoice_item_id:
      fallbackRow?.invoice_item_id ??
      matchedLine?.invoice_item_id ??
      splitRow?.invoice_item_id ??
      null,
    ndis_support_item_name:
      fallbackRow?.ndis_support_item_name ??
      matchedLine?.ndis_support_item_name ??
      splitRow?.ndis_support_item_name ??
      splitRow?.support_item_name ??
      null,
    ndis_support_item_number:
      fallbackRow?.ndis_support_item_number ??
      matchedLine?.ndis_support_item_number ??
      splitRow?.ndis_support_item_number ??
      splitRow?.support_item_number ??
      null,
    custom_line_item_uuid: mergedCustomUuid,
    item_numbers:
      fallbackRow?.item_numbers ??
      matchedLine?.item_numbers ??
      splitRow?.item_numbers,
    start_time: splitRow?.start_time ?? fallbackRow?.start_time ?? null,
    end_time: splitRow?.end_time ?? fallbackRow?.end_time ?? null,
    timesheet_id: isV5MultiAssignmentTimesheet(shiftDetails)
      ? null
      : splitRow?.timesheet_id ??
        fallbackRow?.timesheet_id ??
        fallbackRow?.id ??
        null,
    ...(rowParticipants?.length ? { participants: rowParticipants } : {}),
    billingSplitMeta: isLocalInsert
      ? {
          ...(preservedBillingSplitMeta || {}),
          id: undefined,
          split_shift_id: undefined,
          participants: rowParticipants?.length
            ? rowParticipants
            : preservedBillingSplitMeta?.participants ?? [],
        }
      : getBillingSplitMeta(shiftDetails, index, {
          ...fallbackRow,
          ...splitRow,
          split_shift_id: splitId,
          ...(rowParticipants?.length ? { participants: rowParticipants } : {}),
          billingSplitMeta: preservedBillingSplitMeta,
        }) ?? preservedBillingSplitMeta,
    ...(fallbackRow?._clientKey || splitRow?._clientKey
      ? { _clientKey: fallbackRow?._clientKey || splitRow?._clientKey }
      : {}),
  };
};

export const buildSplitBreakdownRows = (shiftDetails, timesheetBreakdown) => {
  if (!isSplitBillingMode(shiftDetails)) return [];

  const splitDefinition = getBillingSplits(shiftDetails);
  const breakdownList = Array.isArray(timesheetBreakdown) ? timesheetBreakdown : [];
  const invoiceList = getBillingLines(shiftDetails);
  const fallbackRows =
    breakdownList.length >= invoiceList.length ? breakdownList : invoiceList;

  if (splitDefinition.length === 0) {
    // Legacy basic split: prefer saved invoice lines over a longer DEN preview.
    if (!isV5MultiAssignmentTimesheet(shiftDetails) && invoiceList.length > 0) {
      return invoiceList;
    }
    return fallbackRows;
  }

  // Advance only: local "Split breakdown" can grow rows beyond persisted splits.
  // Legacy basic split must stay 1:1 with split_shifts — DEN preview can have
  // more day/evening/night rows and must not invent extra uncoded lines.
  if (
    isV5MultiAssignmentTimesheet(shiftDetails) &&
    breakdownList.length > splitDefinition.length
  ) {
    return breakdownList.map((fallbackRow, index) => {
      const splitRow = splitDefinition[index] ?? {};
      const matchedLine = findBillingLineForSplit(invoiceList, splitRow, index);
      return mergeSplitBreakdownRow(
        shiftDetails,
        index,
        splitRow,
        fallbackRow,
        matchedLine
      );
    });
  }

  return splitDefinition.map((splitRow, index) => {
    const matchedLine = findBillingLineForSplit(invoiceList, splitRow, index);
    // Prefer positional breakdown/local row so a newly split segment stays with
    // its participant instead of inheriting another participant's matched line.
    const positionalRow = fallbackRows[index];
    const fallbackRow =
      (positionalRow?._clientKey ||
        positionalRow?.billingSplitMeta?.participants?.length ||
        positionalRow?.participants?.length
        ? positionalRow
        : null) ??
      matchedLine ??
      positionalRow ??
      {};
    return mergeSplitBreakdownRow(
      shiftDetails,
      index,
      splitRow,
      fallbackRow,
      matchedLine
    );
  });
};

/** Attach V5 billing identifiers to invoice_timesheets[] payload lines. */
export const enrichInvoiceLinePayload = (
  linePayload,
  breakdownRow,
  shiftDetails,
  index,
  { attachParentUuid = true } = {}
) => {
  const isV5 = isV5MultiAssignmentTimesheet(shiftDetails);
  const isLocalInsert = Boolean(breakdownRow?._clientKey);

  // V5: never fall back to array index / billing-line id (unsafe with unsorted payloads).
  // Existing segments must keep split_shift_id; new local segments omit it so BE creates.
  // Legacy: never use invoice-line id as split_shift_id (that silently breaks schedule sync).
  const splitId = isLocalInsert
    ? null
    : breakdownRow?.split_shift_id ??
      breakdownRow?.billingSplitMeta?.split_shift_id ??
      breakdownRow?.billingSplitMeta?.id ??
      getBillingSplits(shiftDetails)[index]?.id ??
      null;

  if (splitId != null && linePayload.split_shift_id == null) {
    linePayload.split_shift_id = splitId;
  }

  if (!isV5) {
    if (breakdownRow?.timesheet_id) {
      linePayload.timesheet_id = breakdownRow.timesheet_id;
    }
    return linePayload;
  }

  if (splitId != null) {
    linePayload.split_shift_id = splitId;
  } else {
    delete linePayload.split_shift_id;
    const participants = getBillingParticipantsForRow(
      shiftDetails,
      index,
      breakdownRow
    );
    const participantIds = participants
      .map((p) => p?.id ?? p?.pt_id ?? p?.participant_id)
      .filter((id) => id != null && id !== "")
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));
    if (participantIds.length > 0) {
      linePayload.participant_ids = participantIds;
    }
  }
  if (attachParentUuid && breakdownRow?.uuid) {
    linePayload.uuid = breakdownRow.uuid;
  }
  if (linePayload.service_type_id == null) {
    if (linePayload.invoice_service_type_id != null) {
      linePayload.service_type_id = linePayload.invoice_service_type_id;
    } else if (breakdownRow?.service_type_id != null) {
      linePayload.service_type_id = breakdownRow.service_type_id;
    } else if (breakdownRow?.invoice_service_type_id != null) {
      linePayload.service_type_id = breakdownRow.invoice_service_type_id;
    }
  }
  return linePayload;
};

/**
 * Build billing start/end datetimes for a single advance-mode segment.
 * Uses segment form clocks with the split row date anchor (overnight-aware).
 */
export const buildAdvanceSegmentBillingDatetimes = (
  breakdownRow,
  segmentStartInput,
  segmentEndInput,
  {
    shiftAnchorDateTime,
    previousSegmentOvernight = false,
    previousBaseDate = null,
  } = {}
) => {
  const rowAnchor = breakdownRow?.start_time ?? shiftAnchorDateTime;
  const segStart =
    segmentStartInput != null && segmentStartInput !== ""
      ? segmentStartInput
      : extractTimeFromDateTime(breakdownRow?.start_time);
  const segEnd =
    segmentEndInput != null && segmentEndInput !== ""
      ? segmentEndInput
      : extractTimeFromDateTime(breakdownRow?.end_time);

  if (
    (!segStart || !segEnd) &&
    breakdownRow?.start_time &&
    breakdownRow?.end_time
  ) {
    return {
      start_time: breakdownRow.start_time,
      end_time: breakdownRow.end_time,
      duration: calculateDuration(breakdownRow.start_time, breakdownRow.end_time),
      isOvernight: false,
      baseDate: moment(breakdownRow.start_time).format("YYYY-MM-DD"),
    };
  }

  let baseDate = previousBaseDate
    ? moment(previousBaseDate).format("YYYY-MM-DD")
    : moment(rowAnchor ?? shiftAnchorDateTime).format("YYYY-MM-DD");

  if (previousSegmentOvernight && previousBaseDate) {
    baseDate = moment(previousBaseDate).add(1, "day").format("YYYY-MM-DD");
  }

  const dateAnchor = rowAnchor ?? shiftAnchorDateTime;
  const isOvernight =
    isCheckNextDayShift(dateAnchor, segStart, segEnd) !== false;

  const start_time = convertTimeToDatetimeLocal(baseDate, segStart);
  const end_time = isOvernight
    ? isCheckNextDayShift(baseDate, segStart, segEnd)
    : convertTimeToDatetimeLocal(baseDate, segEnd);

  return {
    start_time,
    end_time,
    duration: calculateDuration(start_time, end_time),
    isOvernight,
    baseDate,
  };
};

/**
 * Whether a billing line should be saved with the root worker window instead of
 * per-segment day/evening/night clocks.
 *
 * Multi-segment auto-breakdown must keep segment times — segment clocks differ
 * from the root window by design and must never be collapsed on save.
 */
export const shouldUseWorkerWindowForBillingLine = ({
  scheduleTime,
  billingWindowChanged,
  pendingSplitsPayload,
  isAdvanceMode = false,
  invoiceBreakdownEnabled = false,
  breakdownSegmentCount = 0,
}) => {
  if (scheduleTime) return false;
  if (isAdvanceMode) return false;
  // Day/evening/night (or multi-row) breakdown keeps per-segment times.
  if (invoiceBreakdownEnabled && breakdownSegmentCount > 1) return false;
  if (pendingSplitsPayload?.splits?.length) return true;
  // Single-line billing: sync the line to the new worker window after a root edit.
  if (billingWindowChanged) return true;
  return false;
};

/**
 * Scheduled-mode equivalent of shouldUseWorkerWindowForBillingLine.
 * Basic shifts still follow SP scheduled preference for the root window, but
 * auto-breakdown segments must keep their own clocks.
 */
export const shouldUseScheduleWindowForBillingLine = ({
  scheduleTime,
  billingWindowChanged = false,
  pendingSplitsPayload,
  isAdvanceMode = false,
  invoiceBreakdownEnabled = false,
  breakdownSegmentCount = 0,
}) => {
  if (!scheduleTime) return false;
  if (isAdvanceMode) return false;
  // Day/evening/night (or multi-row) breakdown keeps per-segment times.
  if (invoiceBreakdownEnabled && breakdownSegmentCount > 1) return false;
  if (pendingSplitsPayload?.splits?.length) return true;
  // Single-line / collapsed basic split: sync the line to the new schedule window.
  if (billingWindowChanged) return true;
  return false;
};

/** Re-sync editor clocks and breakdown rows after V5 billing save. */
export const getEditorStateFromBillingSave = (shiftDetails, scheduleTime) => {
  const lines = sortBillingRowsByStartTime(getBillingLines(shiftDetails));
  const committed = {
    start: normalizeClockTimeInput(
      extractTimeFromDateTime(
        scheduleTime ? shiftDetails?.start_time : shiftDetails?.worker_start_time
      )
    ),
    end: normalizeClockTimeInput(
      extractTimeFromDateTime(
        scheduleTime ? shiftDetails?.end_time : shiftDetails?.worker_end_time
      )
    ),
  };
  const breakdownRows = buildSplitBreakdownRows(shiftDetails, lines);
  const segmentClocks = breakdownRows.map((row) => ({
    start: normalizeClockTimeInput(extractTimeFromDateTime(row?.start_time)),
    end: normalizeClockTimeInput(extractTimeFromDateTime(row?.end_time)),
  }));
  return { lines, committed, segmentClocks, breakdownRows };
};

const formatActiveHoursDurationInput = (duration) => {
  if (!duration) return "";
  const parts = String(duration).split(":");
  return `${parts[0] || "0"}:${parts[1] || "00"}`;
};

/** Restore breakdown form fields from saved billing lines (incl. sleepover + active hours). */
export const syncBreakdownFormAfterBillingSave = (
  shiftDetails,
  timesheetBreakdown,
  setValue
) => {
  // Split/advance: merge against split definitions. Basic breakdown: use saved lines
  // directly (buildSplitBreakdownRows returns [] when not split billing mode).
  const splitRows = buildSplitBreakdownRows(shiftDetails, timesheetBreakdown);
  const rows =
    splitRows.length > 0
      ? splitRows
      : sortBillingRowsByStartTime(
          Array.isArray(timesheetBreakdown) ? timesheetBreakdown : []
        );

  rows.forEach((row, index) => {
    if (row?.start_time) {
      setValue(
        `start_time_${index}`,
        normalizeClockTimeInput(extractTimeFromDateTime(row.start_time))
      );
    }
    if (row?.end_time) {
      setValue(
        `end_time_${index}`,
        normalizeClockTimeInput(extractTimeFromDateTime(row.end_time))
      );
    }

    setValue(
      `sleepover_${index}`,
      parseBooleanVal(row?.sleepover ?? row?.is_sleepover)
    );

    const serviceTypeId =
      row?.service_type_id ?? row?.invoice_service_type_id ?? null;
    if (serviceTypeId != null) {
      setValue(`invoice_service_type_id_${index}`, serviceTypeId);
    }

    const customLineItemUuid =
      row?.custom_line_item_uuid ?? row?.custom_line_item?.uuid ?? null;
    if (customLineItemUuid) {
      setValue(`invoice_item_id_${index}`, `custom:${customLineItemUuid}`);
    } else if (row?.invoice_item_id != null && row?.invoice_item_id !== "") {
      setValue(`invoice_item_id_${index}`, row.invoice_item_id);
    }

    const hasLine =
      row?.invoice_item_id ||
      customLineItemUuid ||
      row?.custom_line_item?.uuid;
    if (hasLine && parseBooleanVal(row?.alternate_rate)) {
      setValue(`invoice_ndis_rate_${index}`, "alternate");
      const rateAmount = resolvePersistedBreakdownUnitRate(row);
      if (rateAmount != null) {
        setValue(`rate_amount_${index}`, rateAmount);
      }
    } else if (hasLine) {
      const rate = resolvePersistedBreakdownUnitRate(row);
      if (rate != null) {
        setValue(`invoice_ndis_rate_${index}`, rate);
        setValue(`rate_amount_${index}`, rate);
      }
    }

    const child = Array.isArray(row?.children) ? row.children[0] : null;
    if (child) {
      setValue(`active_hours_${index}`, formatActiveHoursDurationInput(child.duration));
      setValue(
        `invoice_item_id_active_hours${index}`,
        child.invoice_item_id ?? null
      );
      if (parseBooleanVal(child?.alternate_rate)) {
        setValue(`invoice_ndis_rate_active_hours${index}`, "alternate");
        if (child?.invoice_price != null && child?.invoice_price !== "") {
          setValue(`rate_amount_active_hours${index}`, child.invoice_price);
        }
      } else if (child?.invoice_price != null && child?.invoice_price !== "") {
        setValue(`invoice_ndis_rate_active_hours${index}`, child.invoice_price);
        setValue(`rate_amount_active_hours${index}`, child.invoice_price);
      } else if (child?.ndis_rate != null && child?.ndis_rate !== "") {
        setValue(`invoice_ndis_rate_active_hours${index}`, child.ndis_rate);
        setValue(`rate_amount_active_hours${index}`, child.ndis_rate);
      }
    }
  });

  return rows;
};

const parseShiftAmount = (value) => {
  if (value == null || value === "") return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** KM/travel preview for billing-allocation list rows and detail payloads. */
export const resolveShiftTravelPreviewTotal = (row) => {
  const fromApi = parseShiftAmount(row?.km_total);
  if (fromApi != null && fromApi > 0) {
    return fromApi;
  }

  if (isV5MultiAssignmentTimesheet(row)) {
    const travelRows = getOptedInParticipantTravel(row);
    if (!travelRows.length) return 0;

    return truncateDecimal(
      travelRows.reduce((sum, travelRow) => {
        const amount = travelShareAmount(travelRow);
        return sum + (Number.isFinite(amount) ? amount : 0);
      }, 0)
    );
  }

  if (!parseBooleanVal(row?.apply_km)) return 0;

  const km = parseFloat(row?.km);
  if (!Number.isFinite(km) || km <= 0) return 0;

  const useAlternateRate = row?.km_alternative_rate;
  const unitPrice = useAlternateRate
    ? parseFloat(row?.km_price) || 0
    : parseFloat(row?.travel_item?.price) || parseFloat(row?.km_price) || 0;

  if (unitPrice <= 0) return 0;

  const participants = row?.participants?.length ?? 1;
  return truncateDecimal((km * unitPrice) / participants);
};

/** Service line total plus travel/KM preview from the invoice-creation API. */
const resolveShiftLinesPlusTravelTotal = (row) => {
  const combined = parseShiftAmount(row?.total_amount);
  if (combined != null) {
    return combined;
  }

  const lineTotal = parseShiftAmount(row?.billing?.total_amount);
  if (lineTotal != null) {
    return truncateDecimal(lineTotal + resolveShiftTravelPreviewTotal(row));
  }

  return null;
};

export const resolveShiftListAmount = (row) => resolveShiftLinesPlusTravelTotal(row);

/**
 * Billable total for create-invoice list rows.
 * Prefer API combined total (service lines + travel); fall back to line recalculation for legacy rows only.
 */
export const resolveShiftBillableTotal = (row) => {
  const linesPlusTravel = resolveShiftLinesPlusTravelTotal(row);
  if (linesPlusTravel != null) {
    return linesPlusTravel;
  }

  // V5 / coded shifts: API totals are authoritative — do not client-recalculate (ratio would be wrong).
  if (isV5MultiAssignmentTimesheet(row) || row?.billing?.has_lines) {
    return null;
  }

  if (!getInvoiceTimesheetLines(row).length) {
    return null;
  }

  const participantCount =
    row?.participants?.length ?? row?.total_participants ?? 1;
  return rosterInvoiceTimesheetTotal(row, participantCount);
};

/** Authoritative create-invoice readiness (prefer API invoice_ready; never treat shells alone as ready). */
export const isShiftInvoiceReady = (row) => {
  if (row?.invoice_non_billable || row?.billing?.invoice_non_billable) {
    return true;
  }
  if (typeof row?.invoice_ready === "boolean") {
    return row.invoice_ready;
  }
  if (row?.billing?.has_lines) {
    return true;
  }
  const total = resolveShiftBillableTotal(row);
  return Number(total) > 0;
};

export const resolveShiftListDuration = (row) =>
  row?.billing?.billing_duration ?? row?.timesheet_length ?? null;

export const resolveShiftListDate = (row) =>
  row?.shift_date ?? row?.time_sheet_date ?? null;

export const formatShiftListWorkerNames = (row) => {
  const workers = Array.isArray(row?.workers) ? row.workers : [];
  if (workers.length === 0 && row?.employee_profile) {
    return `${row.employee_profile?.first_name || ""} ${row.employee_profile?.last_name || ""}`.trim();
  }
  return workers
    .map((worker) =>
      `${worker?.first_name || ""} ${worker?.last_name || ""}`.trim()
    )
    .filter(Boolean)
    .join(", ");
};

const TIMESHEET_STATUS_LABELS = {
  approved: "Approved",
  pending: "Pending",
  rejected: "Rejected",
  expired: "Expired",
  "in-progress": "In Progress",
  "in progress": "In Progress",
  assigned: "No timesheet",
  discarded: "Discarded",
  "n/a": "N/A",
};

const formatWorkerDisplayName = (worker) =>
  `${worker?.first_name || ""} ${worker?.last_name || ""}`.trim() || "Unknown worker";

export const resolveWorkerTimesheetStatus = (worker) => {
  if (!worker) return null;
  if (worker.timesheet_status) return worker.timesheet_status;
  if (!worker.timesheet_uuid) return "assigned";
  return worker.payroll_status ?? worker.status ?? null;
};

export const formatTimesheetStatusLabel = (status) => {
  if (status == null || status === "") return "—";
  const key = String(status).trim();
  return (
    TIMESHEET_STATUS_LABELS[key] ||
    TIMESHEET_STATUS_LABELS[key.toLowerCase()] ||
    key.replace(/[-_]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

/** Case/format-insensitive key so Pending/pending and approved/pay-approved match. */
export const normalizeTimesheetStatusKey = (status) => {
  const key = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_ ]+/g, "-");
  if (key === "pay-approved") return "approved";
  return key;
};

export const getTimesheetStatusBadgeClass = (status, isPartial = false) => {
  if (isPartial) return "bs-badge--partial";
  const key = normalizeTimesheetStatusKey(status);
  if (key === "approved") return "bs-badge--approved";
  if (key === "pending") return "bs-badge--pending";
  if (key === "in-progress") return "bs-badge--inprogress";
  if (key === "rejected") return "bs-badge--rejected";
  if (key === "expired") return "bs-badge--expired";
  return "bs-badge--pending";
};

export const resolveShiftListTimesheetWorkers = (row) => {
  if (Array.isArray(row?.workers) && row.workers.length > 0) {
    return row.workers;
  }
  if (row?.employee_profile) {
    return [
      {
        ...row.employee_profile,
        timesheet_uuid: row?.anchor_timesheet_uuid ?? row?.uuid ?? null,
        timesheet_status: row?.timesheet_status ?? row?.status ?? null,
        payroll_status: row?.payroll_status ?? row?.status ?? null,
      },
    ];
  }
  return [];
};

/**
 * List-cell presentation: show the shared timesheet status when every linked
 * timesheet matches. "Partial" is reserved for genuinely mixed statuses (hover
 * still lists each worker).
 */
export const getShiftListTimesheetStatus = (row) => {
  const workers = resolveShiftListTimesheetWorkers(row).map((worker) => {
    const status = worker?.is_approved
      ? "approved"
      : resolveWorkerTimesheetStatus(worker);
    return {
      workerId: worker?.worker_id ?? worker?.id ?? null,
      timesheetUuid: worker?.timesheet_uuid ?? null,
      name: formatWorkerDisplayName(worker),
      status,
      label: formatTimesheetStatusLabel(status),
      badgeClass: getTimesheetStatusBadgeClass(status),
    };
  });

  if (workers.length === 0) {
    return {
      label: "—",
      status: null,
      isPartial: false,
      badgeClass: "bs-badge--locked",
      workers,
    };
  }

  const linkedWorkers = workers.filter((worker) => worker.timesheetUuid);
  const statusWorkers = linkedWorkers.length > 0 ? linkedWorkers : workers;
  const uniqueKeys = [
    ...new Set(
      statusWorkers.map((worker) => normalizeTimesheetStatusKey(worker.status))
    ),
  ];
  const isPartial = uniqueKeys.length > 1;
  const primary = statusWorkers[0];

  if (isPartial) {
    return {
      label: "Partial",
      status: "partial",
      isPartial: true,
      badgeClass: getTimesheetStatusBadgeClass(null, true),
      workers,
    };
  }

  return {
    label: primary.label,
    status: primary.status,
    isPartial: false,
    badgeClass: getTimesheetStatusBadgeClass(primary.status),
    workers,
  };
};

export const getShiftBillingModeLabel = (row) => {
  if (
    row?.billing_mode === "advance" ||
    row?.is_v5_multi_assignment === true ||
    row?.is_multi_assignment === true
  ) {
    return "Advance";
  }
  if (row?.billing_mode === "split" || row?.is_split_shift) {
    return "Split";
  }
  return "Standard";
};

export const getShiftListBillingStatus = (row) => {
  if (row?.billing?.is_locked) {
    return { label: "Invoiced", tone: "locked" };
  }
  if (typeof row?.invoice_ready === "boolean") {
    if (row.invoice_ready) {
      return { label: "Ready for extraction", tone: "success" };
    }
    if (row?.invoice_ready_status === "needs_code_allocation") {
      return { label: "Needs code allocation", tone: "warning" };
    }
    return { label: "Needs allocation", tone: "warning" };
  }
  if (row?.invoice_ready_status === "ready_for_extraction") {
    return { label: "Ready for extraction", tone: "success" };
  }
  if (row?.invoice_ready_status === "needs_code_allocation") {
    return { label: "Needs code allocation", tone: "warning" };
  }
  if (row?.billing?.has_lines) {
    return { label: "Codes assigned", tone: "success" };
  }
  return { label: "Needs allocation", tone: "warning" };
};

export const getShiftListBillingStatusClass = (tone) => {
  const toneMap = {
    success: "bs-badge--coded",
    warning: "bs-badge--pending",
    locked: "bs-badge--locked",
  };
  return toneMap[tone] ?? "bs-badge--pending";
};

export const formatShiftBillingCurrency = (amount) => {
  const parsed = parseFloat(amount);
  if (Number.isNaN(parsed)) return null;
  return `$${formatAmount(parsed.toFixed(2))}`;
};

export const formatShiftBillingHours = (hours) => {
  if (!hours) return "0 hrs";
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded} ${rounded === 1 ? "hr" : "hrs"}`;
};

/** Aggregate billable metrics for the current list page (no extra API). */
export const summarizeShiftBillingListPage = (rows = []) => {
  let pageTotalAmount = 0;
  let pageTotalHours = 0;
  let codedCount = 0;
  let needsCodesCount = 0;
  let codedAmount = 0;
  let pendingAmount = 0;
  let payrollReadyCount = 0;
  let payrollPendingCount = 0;

  rows.forEach((row) => {
    const amount = parseFloat(resolveShiftListAmount(row) ?? 0);
    const safeAmount = Number.isNaN(amount) ? 0 : amount;
    pageTotalAmount += safeAmount;

    const duration = resolveShiftListDuration(row);
    if (duration) {
      pageTotalHours += parseFloat(timeToQuantity(duration)) || 0;
    }

    if (isShiftInvoiceReady(row)) {
      codedCount += 1;
      codedAmount += safeAmount;
    } else {
      needsCodesCount += 1;
      pendingAmount += safeAmount;
    }

    const payroll = row?.payroll_summary;
    if (payroll?.total != null && payroll.total > 0) {
      if (payroll.approved === payroll.total) {
        payrollReadyCount += 1;
      } else {
        payrollPendingCount += 1;
      }
    }
  });

  const pageCount = rows.length;

  return {
    pageTotalAmount,
    pageTotalHours,
    codedCount,
    needsCodesCount,
    codedAmount,
    pendingAmount,
    payrollReadyCount,
    payrollPendingCount,
    pageCount,
    allocationPercent: pageCount
      ? Math.round((codedCount / pageCount) * 100)
      : 0,
  };
};

/** Aggregate roster invoice metrics for the current list page (no extra API). */
export const summarizeRosterInvoiceListPage = (invoices = []) => {
  let pageTotalAmount = 0;
  let totalTimesheets = 0;

  invoices.forEach((invoice) => {
    const amount = parseFloat(invoice?.total_amount ?? 0);
    if (!Number.isNaN(amount)) {
      pageTotalAmount += amount;
    }
    totalTimesheets += parseInt(invoice?.total_timesheets ?? 0, 10) || 0;
  });

  return {
    pageTotalAmount,
    pageCount: invoices.length,
    totalTimesheets,
  };
};

export const sumSelectedInvoiceAmount = (invoices = [], selectedUuids = []) => {
  const selectedSet = new Set(selectedUuids);
  return invoices.reduce((sum, invoice) => {
    if (!selectedSet.has(invoice?.uuid)) return sum;
    const amount = parseFloat(invoice?.total_amount ?? 0);
    return sum + (Number.isNaN(amount) ? 0 : amount);
  }, 0);
};

/** `response()->success('data', $paginator)` list payload. */
export const parseShiftBillingListResponse = (
  res,
  { fallbackPage = 1, fallbackPerPage = 10 } = {}
) => {
  const paginator = res?.data?.data ?? res?.data ?? {};
  const rows = Array.isArray(paginator?.data)
    ? paginator.data
    : Array.isArray(paginator)
      ? paginator
      : [];

  return {
    rows,
    page: paginator?.current_page ?? fallbackPage,
    per_page: paginator?.per_page ?? fallbackPerPage,
    total: paginator?.total ?? rows.length,
  };
};

/** `response()->success('billing_allocation', $detail)` detail payload. */
export const parseShiftBillingDetailResponse = (res) => {
  const detail =
    res?.data?.billing_allocation ??
    res?.data?.data?.billing_allocation ??
    null;
  return sortV5BillingDetailChronologically(detail);
};

/** `response()->success('breakdown_preview', $preview)` preview payload. */
export const parseShiftBreakdownPreviewResponse = (res) => {
  const preview =
    res?.data?.breakdown_preview ??
    res?.data?.data?.breakdown_preview ??
    null;

  if (!preview) {
    if (Array.isArray(res?.data?.data)) {
      return sortBillingRowsByStartTime(res.data.data);
    }
    if (Array.isArray(res?.data?.lines)) {
      return sortBillingRowsByStartTime(res.data.lines);
    }
    return [];
  }

  if (Array.isArray(preview?.breakdown)) {
    return sortBillingRowsByStartTime(preview.breakdown);
  }
  if (Array.isArray(preview?.lines)) {
    return sortBillingRowsByStartTime(preview.lines);
  }
  return [];
};

/** `response()->success('billing_allocation', $detail)` save payload. */
export const parseShiftBillingSaveResponse = (res) =>
  sortV5BillingDetailChronologically(
    parseShiftBillingDetailResponse(res) ?? res?.data?.data ?? null
  );

/** Query string for V5 shift billing / create-invoice list endpoints. */
export const buildShiftBillingListQuery = ({
  page = 1,
  perPage = 10,
  selectedWorker,
  selectedParticipant,
  selectedTimesheetStatus,
  fromDate,
  toDate,
  billingStatus = "not_invoiced",
  ensureBilling = false,
  autoAllocate = false,
} = {}) => {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("per_page", String(perPage));
  params.set("billing_status", billingStatus);

  if (selectedWorker?.value && selectedWorker.value !== "all") {
    params.set("worker_id", String(selectedWorker.value));
  }
  if (selectedParticipant?.value && selectedParticipant.value !== "all") {
    params.set("participant_id", String(selectedParticipant.value));
  }
  if (selectedTimesheetStatus?.value && selectedTimesheetStatus.value !== "all") {
    params.set("timesheet_status", String(selectedTimesheetStatus.value));
  }
  if (fromDate) params.set("start_date", fromDate);
  if (toDate) params.set("end_date", toDate);
  if (ensureBilling) params.set("ensure_billing", "true");
  if (autoAllocate) params.set("auto_allocate", "true");

  return params.toString();
};

/** Timesheet status filter options for Code Allocation and Invoice Creation screens. */
export const TIMESHEET_STATUS_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Expired", value: "expired" },
  { label: "Rejected", value: "rejected" },
  { label: "Partial", value: "partial" },
];

/** Normalize clock values to HH:mm for comparisons and time inputs. */
export const normalizeClockTimeInput = (time) => {
  if (!time) return "";
  const parsed = moment(time, ["HH:mm", "HH:mm:ss", "h:mm A"], true);
  if (parsed.isValid()) return parsed.format("HH:mm");
  const str = String(time);
  return str.length >= 5 ? str.slice(0, 5) : str;
};

/** Show confirmation when shift window changes and billing segments exist. */
export const shouldConfirmShiftTimeChange = (
  nextStart,
  nextEnd,
  committed = {},
  breakdownRows = [],
  { hasBillingSegments = false } = {}
) => {
  const normStart = normalizeClockTimeInput(nextStart);
  const normEnd = normalizeClockTimeInput(nextEnd);
  if (!normStart || !normEnd) return false;
  if (
    normStart === normalizeClockTimeInput(committed.start) &&
    normEnd === normalizeClockTimeInput(committed.end)
  ) {
    return false;
  }
  return (
    hasBillingSegments ||
    (Array.isArray(breakdownRows) && breakdownRows.length > 0)
  );
};

/** True when any participant has more than one billing segment (custom splits). */
export const hasMultiSegmentBillingSplits = (shiftDetails, breakdownRows = []) => {
  if (!Array.isArray(breakdownRows) || breakdownRows.length === 0) return false;
  const groups = groupBreakdownIndicesByParticipant(shiftDetails, breakdownRows);
  return groups.some((group) => group.indices.length > 1);
};

/** Wall-clock shift window as datetimes using the shift's existing date anchor. */
export const buildShiftWindowDatetimes = (
  shiftDetails,
  startTimeInput,
  endTimeInput,
  scheduleTime
) => {
  const anchor = scheduleTime
    ? shiftDetails?.start_time
    : shiftDetails?.worker_start_time;
  const baseDate = moment(anchor).format("YYYY-MM-DD");

  const startDt = embedDateWithTime(
    startTimeInput,
    convertTimeToDatetimeLocal(baseDate, startTimeInput)
  );

  const spansNextDay =
    isCheckNextDayShift(anchor, startTimeInput, endTimeInput) !== false;
  const endBase = spansNextDay
    ? moment(baseDate).add(1, "day").format("YYYY-MM-DD")
    : baseDate;

  const endDt = embedDateWithTime(
    endTimeInput,
    convertTimeToDatetimeLocal(endBase, endTimeInput)
  );

  return { startDt, endDt };
};

/**
 * Collapse to one segment per participant, each spanning the full shift window.
 */
export const collapseBreakdownRowsToShiftWindow = (
  shiftDetails,
  breakdownRows,
  startDt,
  endDt
) => {
  if (!Array.isArray(breakdownRows) || breakdownRows.length === 0) return [];

  const groups = groupBreakdownIndicesByParticipant(shiftDetails, breakdownRows);
  if (!groups.length) {
    return breakdownRows.map((row) => ({
      ...row,
      start_time: startDt,
      end_time: endDt,
      duration: calculateDuration(startDt, endDt),
    }));
  }

  return groups.map((group) => {
    const firstRow = breakdownRows[group.indices[0]] ?? {};
    return {
      ...firstRow,
      start_time: startDt,
      end_time: endDt,
      duration: calculateDuration(startDt, endDt),
      billingSplitMeta: firstRow.billingSplitMeta
        ? {
            ...firstRow.billingSplitMeta,
            start_time: startDt,
            end_time: endDt,
            duration: calculateDuration(startDt, endDt),
          }
        : firstRow.billingSplitMeta,
    };
  });
};

/** Merge shift window times into shiftDetails (local UI state). */
export const applyShiftWindowToShiftDetails = (
  shiftDetails,
  startDt,
  endDt,
  scheduleTime,
  updatedRows = [],
  { resetSegments = false } = {}
) => {
  const next = { ...shiftDetails };

  if (scheduleTime) {
    next.start_time = startDt;
    next.end_time = endDt;
  } else {
    next.worker_start_time = startDt;
    next.worker_end_time = endDt;
  }

  if (!updatedRows?.length) {
    return next;
  }

  if (resetSegments) {
    if (isV5MultiAssignmentTimesheet(next)) {
      const prevBilling = next.v5_shift_billing ?? {};
      const splits = updatedRows.map((row, index) => {
        const participants = getBillingParticipantsForRow(next, index, row);
        const prevSplit = getBillingSplitMeta(next, index, row) ?? {};
        return {
          ...prevSplit,
          ...row,
          start_time: startDt,
          end_time: endDt,
          participants: participants.length ? participants : prevSplit.participants,
        };
      });
      const prevLines = getBillingLines(next);
      const lines = updatedRows.map((row, index) => ({
        ...(prevLines[index] ?? {}),
        ...row,
        start_time: startDt,
        end_time: endDt,
      }));
      next.v5_shift_billing = { ...prevBilling, splits, lines };
      next.invoiceTimesheets = lines;
    } else if (Array.isArray(next.split_shift) || next.is_split_shift) {
      // Legacy basic split: mirror Advance — collapse splits and invoice lines.
      const lines = updatedRows.map((row) => ({
        ...row,
        start_time: startDt,
        end_time: endDt,
        duration: calculateDuration(startDt, endDt),
      }));
      next.split_shift = lines;
      next.invoiceTimesheets = lines;
    }
    return next;
  }

  if (isV5MultiAssignmentTimesheet(next)) {
    const prevBilling = next.v5_shift_billing ?? {};
    const splits = (prevBilling.splits ?? []).map((split) => ({
      ...split,
      start_time: startDt,
      end_time: endDt,
    }));
    const lines = (getBillingLines(next) ?? []).map((line) => ({
      ...line,
      start_time: startDt,
      end_time: endDt,
    }));
    next.v5_shift_billing = { ...prevBilling, splits, lines };
    next.invoiceTimesheets = lines;
  } else if (Array.isArray(next.split_shift)) {
    next.split_shift = next.split_shift.map((split) => ({
      ...split,
      start_time: startDt,
      end_time: endDt,
    }));
  }

  return next;
};

/** V5 POST /shift/{id}/splits body after collapsing segments to the shift window. */
export const buildV5SplitsStorePayload = (shiftDetails, collapsedRows = []) => {
  const shiftStart = extractTimeFromDateTime(shiftDetails?.start_time);
  const shiftEnd = extractTimeFromDateTime(shiftDetails?.end_time);
  const fullDayByParticipant = new Set();

  return {
    replace: true,
    splits: collapsedRows
      .map((row, index) => {
        const participants = getBillingParticipantsForRow(shiftDetails, index, row);
        const ptId = participants?.[0]?.id ?? participants?.[0]?.pt_id;
        const serviceTypeId =
          row?.service_type_id ??
          row?.invoice_service_type_id ??
          row?.billingSplitMeta?.service_type_id;

        if (!ptId || !serviceTypeId) return null;

        const rowStart = extractTimeFromDateTime(row.start_time);
        const rowEnd = extractTimeFromDateTime(row.end_time);
        const ptKey = String(ptId);
        if (
          isAdvanceUnallocatedSameClockSegment(
            rowStart,
            rowEnd,
            shiftStart,
            shiftEnd,
            { alreadyCountedFullDay: fullDayByParticipant.has(ptKey) }
          )
        ) {
          return null;
        }
        if (isAdvanceSameClockTimeFullDay(rowStart, rowEnd)) {
          fullDayByParticipant.add(ptKey);
        }

        return {
          start_time: row.start_time,
          end_time: row.end_time,
          service_type_id: Number(serviceTypeId),
          participant_ids: [Number(ptId)],
          ratio: row?.ratio ?? row?.billingSplitMeta?.ratio ?? null,
          invoice_item_id: row?.invoice_item_id ?? null,
          ndis_rate: row?.ndis_rate ?? row?.invoice_price ?? null,
          invoice_price: row?.invoice_price ?? row?.ndis_rate ?? null,
          is_sleepover: Boolean(row?.is_sleepover),
        };
      })
      .filter(Boolean),
  };
};

/** Max participant breakdown segments (matches advance shift create). */
export const CODE_ALLOCATION_MAX_BREAKDOWN_SPLITS = 30;

const BREAKDOWN_FORM_FIELD_AT = [
  (i) => `start_time_${i}`,
  (i) => `end_time_${i}`,
  (i) => `invoice_item_id_${i}`,
  (i) => `invoice_ndis_rate_${i}`,
  (i) => `rate_amount_${i}`,
  (i) => `invoice_item_name_${i}`,
  (i) => `applied_rate_${i}`,
  (i) => `sleepover_${i}`,
  (i) => `invoice_service_type_id_${i}`,
  (i) => `ratio_${i}`,
  (i) => `active_hours_${i}`,
  (i) => `invoice_item_id_active_hours${i}`,
  (i) => `invoice_ndis_rate_active_hours${i}`,
  (i) => `rate_amount_active_hours${i}`,
];

const setBreakdownFormField = (setValue, name, value) => {
  setValue(name, value, { shouldDirty: true });
};

/** Shift indexed form fields right so a new segment can be inserted at `insertAt`. */
export const shiftBreakdownFormFieldsAfterInsert = (
  getValues,
  setValue,
  insertAt,
  currentLength
) => {
  if (!getValues || !setValue || currentLength <= insertAt) return;
  for (let i = currentLength - 1; i >= insertAt; i -= 1) {
    BREAKDOWN_FORM_FIELD_AT.forEach((fieldAt) => {
      setBreakdownFormField(setValue, fieldAt(i + 1), getValues(fieldAt(i)));
    });
  }
};

/** Shift indexed form fields left after removing the segment at `removeAt`. */
export const shiftBreakdownFormFieldsAfterRemove = (
  getValues,
  setValue,
  removeAt,
  currentLength
) => {
  if (!getValues || !setValue || currentLength <= 0) return;
  for (let i = removeAt; i < currentLength - 1; i += 1) {
    BREAKDOWN_FORM_FIELD_AT.forEach((fieldAt) => {
      setBreakdownFormField(setValue, fieldAt(i), getValues(fieldAt(i + 1)));
    });
  }
  BREAKDOWN_FORM_FIELD_AT.forEach((fieldAt) => {
    setBreakdownFormField(setValue, fieldAt(currentLength - 1), undefined);
  });
};

/** Copy billing form fields from one breakdown index to another (advance-style clone). */
export const copyBreakdownFormSegment = (
  getValues,
  setValue,
  fromIndex,
  toIndex,
  { clearSleepover = true } = {}
) => {
  if (!getValues || !setValue) return;
  BREAKDOWN_FORM_FIELD_AT.forEach((fieldAt) => {
    setBreakdownFormField(setValue, fieldAt(toIndex), getValues(fieldAt(fromIndex)));
  });
  if (clearSleepover) {
    setBreakdownFormField(setValue, `sleepover_${toIndex}`, false);
    setBreakdownFormField(setValue, `active_hours_${toIndex}`, undefined);
    setBreakdownFormField(
      setValue,
      `invoice_item_id_active_hours${toIndex}`,
      undefined
    );
    setBreakdownFormField(
      setValue,
      `invoice_ndis_rate_active_hours${toIndex}`,
      undefined
    );
    setBreakdownFormField(
      setValue,
      `rate_amount_active_hours${toIndex}`,
      undefined
    );
  }
};

/**
 * Clone a breakdown row for "Split breakdown" — same participant + billing fields,
 * no persisted ids (frontend-only until save via existing invoice_timesheets).
 */
export const cloneBreakdownRowForSegmentSplit = (
  sourceRow,
  shiftDetails,
  sourceIndex
) => {
  const participants = getBillingParticipantsForRow(
    shiftDetails,
    sourceIndex,
    sourceRow
  );
  const splitMeta =
    sourceRow?.billingSplitMeta ??
    getBillingSplitMeta(shiftDetails, sourceIndex, sourceRow) ??
    {};

  return {
    ...sourceRow,
    uuid: null,
    timesheet_id: null,
    split_shift_id: null,
    id: null,
    sleepover: false,
    is_sleepover: false,
    children: [],
    participants,
    billingSplitMeta: {
      ...splitMeta,
      id: undefined,
      split_shift_id: undefined,
      participants,
    },
    _clientKey: `local-split-${Date.now()}-${sourceIndex}`,
  };
};

/** Sync local V5 split/line definitions after a frontend-only segment insert/remove. */
export const applyLocalBreakdownRowsToShiftDetails = (shiftDetails, rows = []) => {
  if (!shiftDetails || !Array.isArray(rows)) return shiftDetails;
  if (!isV5MultiAssignmentTimesheet(shiftDetails)) return shiftDetails;

  const prevBilling = shiftDetails.v5_shift_billing ?? {};
  const splits = rows.map((row, index) => {
    const participants = getBillingParticipantsForRow(shiftDetails, index, row);
    const prevSplit =
      row?.billingSplitMeta ??
      getBillingSplitMeta(shiftDetails, index, row) ??
      {};
    const {
      id: _prevId,
      split_shift_id: _prevSplitId,
      ...restPrevSplit
    } = prevSplit;
    const splitId = row?._clientKey
      ? null
      : row?.split_shift_id ?? row?.id ?? null;
    return {
      ...restPrevSplit,
      start_time: row?.start_time ?? prevSplit?.start_time ?? null,
      end_time: row?.end_time ?? prevSplit?.end_time ?? null,
      service_type_id:
        row?.service_type_id ??
        row?.invoice_service_type_id ??
        prevSplit?.service_type_id,
      invoice_service_type_id:
        row?.invoice_service_type_id ??
        row?.service_type_id ??
        prevSplit?.invoice_service_type_id,
      invoice_item_id: row?.invoice_item_id ?? prevSplit?.invoice_item_id ?? null,
      ratio: row?.ratio ?? prevSplit?.ratio ?? null,
      participants: participants.length
        ? participants
        : prevSplit?.participants ?? row?.participants ?? [],
      is_sleepover: parseBooleanVal(row?.sleepover ?? row?.is_sleepover),
      ...(splitId != null ? { id: splitId, split_shift_id: splitId } : {}),
      ...(row?._clientKey ? { _clientKey: row._clientKey } : {}),
    };
  });

  const lines = rows.map((row) => {
    const isLocalInsert = Boolean(row?._clientKey);
    return {
      ...row,
      uuid: isLocalInsert ? null : row?.uuid ?? null,
      split_shift_id: isLocalInsert
        ? null
        : row?.split_shift_id ?? row?.id ?? null,
      id: isLocalInsert ? null : row?.id ?? null,
    };
  });

  return {
    ...shiftDetails,
    v5_shift_billing: {
      ...prevBilling,
      splits,
      lines,
    },
    invoiceTimesheets: lines,
  };
};
